import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Schedule, ScheduleTarget } from '@kioskforge/shared';
import { resolveNow } from '@kioskforge/schedule';
import { Badge, Button, Input, Select, Table } from '../components/ui';
import { ConfirmDialog, EmptyStateBlock, ErrorState, LoadingState } from '../components/states';
import { useToast } from '../app/ToastProvider';
import { useSession } from '../app/session';
import { ApiError } from '../api/client';
import { listLayouts } from '../api/layouts';
import { createPlaylist, deletePlaylist, listPlaylists, publishPlaylist, type PlaylistDto } from '../api/playlists';
import { listScreens, patchScreen, type ScreenDto } from '../api/screens';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

type TargetKind = 'playlist' | 'layout';

type BlockDraft = {
  days: number[];
  from: string;
  to: string;
  targetKind: TargetKind;
  targetId: string;
  priority: number;
  label: string;
};

type OverrideDraft = { date: string; targetKind: TargetKind; targetId: string; label: string };

function emptyBlock(): BlockDraft {
  return { days: [1, 2, 3, 4, 5], from: '09:00', to: '17:00', targetKind: 'playlist', targetId: '', priority: 0, label: '' };
}

/** Today, plus/minus days, as YYYY-MM-DD (local). */
function dayOffset(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** A local datetime string for the `datetime-local` control. */
function localInputValue(days: number, hour: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function targetOf(kind: TargetKind, id: string): ScheduleTarget {
  return kind === 'playlist' ? { kind: 'playlist', playlist_id: id } : { kind: 'layout', layout_id: id };
}

/**
 * X2.3 — Playlists & schedule.
 *
 * Build playlists (ordered items), build a screen's 7-day grid (time blocks →
 * playlist or layout, priority, date overrides) and preview "what shows at
 * instant T" using the **same** `@kioskforge/schedule` resolver the server and
 * player use, so the preview cannot disagree with a device.
 */
export function Schedule() {
  const { session } = useSession();
  const { notify, notifyError } = useToast();
  const writable = (session?.role ?? 'viewer') !== 'viewer';

  const [playlists, setPlaylists] = useState<PlaylistDto[]>([]);
  const [screens, setScreens] = useState<ScreenDto[]>([]);
  const [layouts, setLayouts] = useState<Array<{ id: string; name?: string; status?: string }>>([]);
  const [retryNote, setRetryNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [playlistName, setPlaylistName] = useState('');
  const [playlistItems, setPlaylistItems] = useState<Array<{ layoutId: string; duration_s: number }>>([{ layoutId: '', duration_s: 20 }]);
  const [busy, setBusy] = useState('');

  const [selectedScreen, setSelectedScreen] = useState('');
  const [blocks, setBlocks] = useState<BlockDraft[]>([]);
  const [overrides, setOverrides] = useState<OverrideDraft[]>([]);
  const [fallbackKind, setFallbackKind] = useState<TargetKind>('playlist');
  const [fallbackId, setFallbackId] = useState('');
  const [previewAt, setPreviewAt] = useState(localInputValue(0, 12));
  const [deleteTarget, setDeleteTarget] = useState<PlaylistDto | null>(null);

  const load = useCallback(async (): Promise<boolean> => {
    // FB-0.7 / CC-4: a transient failure retries with backoff and, while it
    // does, shows a clear transient message — never a raw "Failed to fetch".
    const attempts = 3;
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        const [pls, scrs, lays] = await Promise.all([listPlaylists(), listScreens(), listLayouts()]);
        setPlaylists(pls);
        setScreens(scrs);
        setLayouts(lays);
        setError('');
        setRetryNote('');
        return true;
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : 'Could not load schedules.';
        const transient = attempt < attempts;
        if (transient) {
          setRetryNote('The connection was interrupted — retrying…');
          await new Promise((resolve) => setTimeout(resolve, 500 * attempt));
        } else {
          setError(
            /failed to fetch|network/i.test(message)
              ? 'Could not reach the server just now. Check the connection and try again.'
              : message
          );
        }
      }
    }
    return false;
  }, []);

  useEffect(() => {
    void load().finally(() => setLoading(false));
  }, [load]);

  const publishedLayouts = useMemo(() => layouts.filter((l) => l.status === 'published'), [layouts]);
  const screen = screens.find((s) => s.id === selectedScreen) ?? null;

  // Load the selected screen's schedule into the grid.
  useEffect(() => {
    if (!screen) return;
    const schedule = screen.schedule as Schedule | null;
    setFallbackKind(schedule?.fallback?.kind ?? 'playlist');
    setFallbackId(schedule?.fallback ? (schedule.fallback.kind === 'playlist' ? schedule.fallback.playlist_id : schedule.fallback.layout_id) : '');
    setBlocks(
      (schedule?.blocks ?? []).map((b) => ({
        days: b.days,
        from: b.from,
        to: b.to,
        targetKind: b.target.kind,
        targetId: b.target.kind === 'playlist' ? b.target.playlist_id : b.target.layout_id,
        priority: b.priority ?? 0,
        label: b.label ?? ''
      }))
    );
    setOverrides(
      (schedule?.overrides ?? []).map((o) => ({
        date: o.date,
        targetKind: o.target.kind,
        targetId: o.target.kind === 'playlist' ? o.target.playlist_id : o.target.layout_id,
        label: o.label ?? ''
      }))
    );
  }, [screen]);

  function targetLabel(target: ScheduleTarget): string {
    if (target.kind === 'playlist') return playlists.find((p) => p.id === target.playlist_id)?.name ?? target.playlist_id;
    return layouts.find((l) => l.id === target.layout_id)?.name ?? target.layout_id;
  }

  /** Build the Schedule object from the current grid (validated by the resolver). */
  function buildSchedule(): Schedule | null {
    if (!screen) return null;
    return {
      timezone: screen.timezone,
      blocks: blocks
        .filter((b) => b.days.length > 0 && b.from && b.to && b.targetId)
        .map((b, index) => ({
          id: `b${index + 1}`,
          days: b.days,
          from: b.from,
          to: b.to,
          target: targetOf(b.targetKind, b.targetId),
          priority: b.priority,
          ...(b.label ? { label: b.label } : {})
        })),
      overrides: overrides
        .filter((o) => o.date && o.targetId)
        .map((o) => ({ date: o.date, target: targetOf(o.targetKind, o.targetId), priority: 0, ...(o.label ? { label: o.label } : {}) })),
      fallback: targetOf(fallbackKind, fallbackId || 'none')
    };
  }

  // The preview uses the shared resolver — identical to the server/player answer.
  const preview = useMemo(() => {
    const schedule = buildSchedule();
    if (!schedule || !fallbackId) return null;
    try {
      return resolveNow(schedule, new Date(previewAt));
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, blocks, overrides, fallbackKind, fallbackId, previewAt]);

  async function createPlaylistNow() {
    const items = playlistItems
      .filter((item) => item.layoutId)
      .map((item, index) => ({
        id: `i${index + 1}`,
        kind: 'layout' as const,
        layout_id: item.layoutId,
        duration_s: item.duration_s,
        transition: 'fade' as const
      }));
    if (!playlistName.trim() || items.length === 0) return;
    setBusy('playlist');
    try {
      const created = await createPlaylist({ name: playlistName.trim(), items });
      notify(`Playlist “${created.name}” created`);
      setPlaylistName('');
      setPlaylistItems([{ layoutId: '', duration_s: 20 }]);
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not create the playlist.');
    } finally {
      setBusy('');
    }
  }

  async function publish(id: string) {
    setBusy(id);
    try {
      await publishPlaylist(id);
      notify('Published — the ETag changed');
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not publish.');
    } finally {
      setBusy('');
    }
  }

  async function removePlaylist() {
    if (!deleteTarget) return;
    try {
      await deletePlaylist(deleteTarget.id);
      notify(`Deleted “${deleteTarget.name}”`);
      setDeleteTarget(null);
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not delete.');
    }
  }

  async function saveSchedule() {
    const schedule = buildSchedule();
    if (!screen || !schedule) return;
    if (!fallbackId) {
      notify('Choose a fallback first.');
      return;
    }
    setBusy('schedule');
    try {
      await patchScreen(screen.id, { schedule });
      notify('Schedule saved');
      await load();
    } catch (cause) {
      notifyError(cause instanceof ApiError ? cause.message : 'Could not save the schedule.');
    } finally {
      setBusy('');
    }
  }

  function targetOptions() {
    return (
      <>
        <option value="">Choose…</option>
        {playlists.map((p) => <option key={`p-${p.id}`} value={p.id}>Playlist: {p.name}</option>)}
        {publishedLayouts.map((l) => <option key={`l-${l.id}`} value={l.id}>Layout: {l.name ?? l.id}</option>)}
      </>
    );
  }

  function targetPicker(value: string, kind: TargetKind, onPick: (kind: TargetKind, id: string) => void, key: string) {
    return (
      <>
        <Select aria-label={`${key} kind`} value={kind} disabled={!writable} onChange={(event) => onPick(event.target.value as TargetKind, value)}>
          <option value="playlist">Playlist</option>
          <option value="layout">Layout</option>
        </Select>
        <Select aria-label={`${key} target`} value={value} disabled={!writable} onChange={(event) => onPick(kind, event.target.value)}>
          {targetOptions()}
        </Select>
      </>
    );
  }

  return (
    <main className="workspace-main">
      <header className="page-header">
        <div>
          <span className="index-label">CONTROL ROOM / PLAYLISTS &amp; SCHEDULE</span>
          <h1>Playlists &amp; schedule</h1>
        </div>
      </header>

      <p className="detail-note">
        A playlist is an ordered set of items; a screen&rsquo;s weekly grid maps time blocks to a
        playlist or layout. The preview resolves with the same engine the server and player use,
        so it cannot disagree with a device.
      </p>

      {loading && <LoadingState label="Loading schedules" />}
      {retryNote && !error && (
        <p className="detail-note" role="status" data-retry-note>
          {retryNote}
        </p>
      )}
      {error && <ErrorState title="Schedules unavailable" detail={error} onRetry={() => void load()} />}

      {!loading && !error && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">NEW PLAYLIST</span>
          </header>
          <div className="panel-body">
            <label className="index-label" htmlFor="pl-name">NAME</label>
            <Input id="pl-name" value={playlistName} disabled={!writable} onChange={(event) => setPlaylistName(event.target.value)} />
            <span className="index-label">ITEMS (LAYOUT → DURATION)</span>
            {playlistItems.map((item, index) => (
              <div className="screen-inline" key={`item-${index}`}>
                <Select
                  aria-label={`item ${index + 1} layout`}
                  value={item.layoutId}
                  disabled={!writable}
                  onChange={(event) => setPlaylistItems((rows) => rows.map((row, i) => (i === index ? { ...row, layoutId: event.target.value } : row)))}
                >
                  <option value="">Choose a layout…</option>
                  {publishedLayouts.map((l) => <option key={l.id} value={l.id}>{l.name ?? l.id}</option>)}
                </Select>
                <Input
                  aria-label={`item ${index + 1} duration`}
                  type="number"
                  inputMode="numeric"
                  value={item.duration_s}
                  disabled={!writable}
                  onChange={(event) => setPlaylistItems((rows) => rows.map((row, i) => (i === index ? { ...row, duration_s: Number(event.target.value) || 1 } : row)))}
                />
                <Button variant="quiet" type="button" disabled={!writable || playlistItems.length === 1} onClick={() => setPlaylistItems((rows) => rows.filter((_, i) => i !== index))}>
                  Remove
                </Button>
              </div>
            ))}
            <div className="settings-actions">
              <Button variant="quiet" type="button" disabled={!writable} onClick={() => setPlaylistItems((rows) => [...rows, { layoutId: '', duration_s: 20 }])}>
                Add item
              </Button>
              <Button type="button" disabled={!writable || busy === 'playlist' || !playlistName.trim()} onClick={() => void createPlaylistNow()}>
                {busy === 'playlist' ? 'Creating…' : 'Create playlist'}
              </Button>
            </div>
          </div>

          {playlists.length > 0 && (
            <Table>
              <thead>
                <tr><th>Playlist</th><th>Items</th><th>Status</th><th>Version</th><th /></tr>
              </thead>
              <tbody>
                {playlists.map((p) => (
                  <tr key={p.id}>
                    <td>{p.name}</td>
                    <td>{p.items?.length ?? 0}</td>
                    <td>{p.status === 'published' ? <Badge>published</Badge> : 'draft'}</td>
                    <td>{p.version}</td>
                    <td>
                      {/* FB-0.5: an empty playlist cannot be published. */}
                      <Button
                        variant="quiet"
                        type="button"
                        disabled={!writable || busy === p.id || (p.items?.length ?? 0) === 0}
                        title={(p.items?.length ?? 0) === 0 ? 'Add at least one item before publishing.' : undefined}
                        onClick={() => void publish(p.id)}
                      >
                        Publish
                      </Button>{' '}
                      <Button variant="quiet" type="button" disabled={!writable} onClick={() => setDeleteTarget(p)}>Delete</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </section>
      )}

      {!loading && !error && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">SCREEN SCHEDULE</span>
          </header>
          <div className="panel-body">
            <label className="index-label" htmlFor="sched-screen">SCREEN</label>
            <Select id="sched-screen" value={selectedScreen} onChange={(event) => setSelectedScreen(event.target.value)}>
              <option value="">Choose a screen</option>
              {screens.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.timezone})</option>)}
            </Select>

            {screen && (
              <>
                <span className="index-label">TIME BLOCKS</span>
                {blocks.map((block, index) => (
                  <div className="screen-inline" key={`block-${index}`}>
                    <div className="day-toggles">
                      {DAYS.map((day, dayIndex) => (
                        <label key={day} className="day-toggle">
                          <input
                            type="checkbox"
                            checked={block.days.includes(dayIndex)}
                            disabled={!writable}
                            onChange={(event) =>
                              setBlocks((rows) => rows.map((row, i) => (i === index
                                ? { ...row, days: event.target.checked ? [...new Set([...row.days, dayIndex])].sort() : row.days.filter((d) => d !== dayIndex) }
                                : row)))
                            }
                          />
                          {day}
                        </label>
                      ))}
                    </div>
                    <Input aria-label={`block ${index + 1} from`} type="time" value={block.from} disabled={!writable} onChange={(event) => setBlocks((rows) => rows.map((row, i) => (i === index ? { ...row, from: event.target.value } : row)))} />
                    <Input aria-label={`block ${index + 1} to`} type="time" value={block.to === '24:00' ? '23:59' : block.to} disabled={!writable} onChange={(event) => setBlocks((rows) => rows.map((row, i) => (i === index ? { ...row, to: event.target.value } : row)))} />
                    {targetPicker(block.targetId, block.targetKind, (kind, id) => setBlocks((rows) => rows.map((row, i) => (i === index ? { ...row, targetKind: kind, targetId: id } : row))), `block ${index + 1}`)}
                    <Input aria-label={`block ${index + 1} priority`} type="number" value={block.priority} disabled={!writable} onChange={(event) => setBlocks((rows) => rows.map((row, i) => (i === index ? { ...row, priority: Number(event.target.value) || 0 } : row)))} />
                    <Button variant="quiet" type="button" disabled={!writable} onClick={() => setBlocks((rows) => rows.filter((_, i) => i !== index))}>Remove</Button>
                  </div>
                ))}
                <div className="settings-actions">
                  <Button variant="quiet" type="button" disabled={!writable} onClick={() => setBlocks((rows) => [...rows, emptyBlock()])}>Add block</Button>
                </div>

                <span className="index-label">DATE OVERRIDES</span>
                {overrides.map((override, index) => (
                  <div className="screen-inline" key={`override-${index}`}>
                    <Input aria-label={`override ${index + 1} date`} type="date" value={override.date} disabled={!writable} onChange={(event) => setOverrides((rows) => rows.map((row, i) => (i === index ? { ...row, date: event.target.value } : row)))} />
                    {targetPicker(override.targetId, override.targetKind, (kind, id) => setOverrides((rows) => rows.map((row, i) => (i === index ? { ...row, targetKind: kind, targetId: id } : row))), `override ${index + 1}`)}
                    <Button variant="quiet" type="button" disabled={!writable} onClick={() => setOverrides((rows) => rows.filter((_, i) => i !== index))}>Remove</Button>
                  </div>
                ))}
                <div className="settings-actions">
                  <Button variant="quiet" type="button" disabled={!writable} onClick={() => setOverrides((rows) => [...rows, { date: dayOffset(0), targetKind: 'playlist', targetId: '', label: '' }])}>Add override</Button>
                </div>

                <span className="index-label">FALLBACK (WHEN NOTHING MATCHES)</span>
                <div className="screen-inline">
                  {targetPicker(fallbackId, fallbackKind, (kind, id) => { setFallbackKind(kind); setFallbackId(id); }, 'fallback')}
                </div>

                <div className="settings-actions">
                  <Button type="button" disabled={!writable || busy === 'schedule'} onClick={() => void saveSchedule()}>
                    {busy === 'schedule' ? 'Saving…' : 'Save schedule'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {!loading && !error && screen && (
        <section className="panel">
          <header className="panel-heading">
            <span className="index-label">PREVIEW — WHAT SHOWS AT</span>
          </header>
          <div className="panel-body">
            <div className="screen-inline">
              <Input aria-label="Preview instant" type="datetime-local" value={previewAt} onChange={(event) => setPreviewAt(event.target.value)} />
            </div>
            <p className="detail-note" data-schedule-preview>
              {preview
                ? `→ ${targetLabel(preview.target)} (source: ${preview.source}, ${new Date(preview.startsAt).toLocaleString()} – ${new Date(preview.endsAt).toLocaleString()})`
                : 'No resolution (set a fallback and a valid grid).'}
            </p>
          </div>
        </section>
      )}

      {!loading && !error && playlists.length === 0 && screens.length === 0 && (
        <EmptyStateBlock title="Nothing to schedule" detail="Create a screen and a layout first, then return here." />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete playlist"
        confirmWord="DELETE"
        detail={`Delete “${deleteTarget?.name ?? ''}”? A schedule referencing it must be repointed.`}
        confirmLabel="Delete"
        onConfirm={() => void removePlaylist()}
        onClose={() => setDeleteTarget(null)}
      />
    </main>
  );
}
